import PlaceSearchPagination = google.maps.places.PlaceSearchPagination;
import PlaceResult = google.maps.places.PlaceResult;

import { PlaceResultCallback, PlaceResultLoader } from './place-result-loader';


/**
 * Wrapper for Google Places API search methods that return paginated result sets. The wrapper exposes promise-based
 * access to the current, previous, and next pages.
 */
export class PlaceResultWrapper {
  places: PlaceResult[];
  pagination: PlaceSearchPagination;

  /**
   * Creates an instance of PlaceResultWrapper, given a method that implements the actual retrieval.
   * @param retrieve {(callback: PlaceResultCallback) => void} Method that implements the actual retrieval of search
   *  results. This method should accept a callback instance, and then call the proper Google Places API method for
   *  retrieving the results. The supplied callback should be passed to the underling Google Places API method.
   * @param [handler] {(results: PlaceResult[]) => PlaceResult[]} Optional handler method for performing post-processing
   *  of the results.
   * @returns {Promise<PlaceResultWrapper>} Promise that resolves to an instance of PlaceResultWrapper.
   */
  static async wrap(
    retrieve: (callback: PlaceResultCallback) => void,
    handler?: (results: PlaceResult[]) => PlaceResult[]
  ): Promise<PlaceResultWrapper> {
    const wrapper = new PlaceResultWrapper(retrieve, handler);
    await wrapper._getResults();
    return wrapper;
  }

  private constructor(
    private _retrieve: (callback: PlaceResultCallback) => void,
    private _handler?: (results: PlaceResult[]) => PlaceResult[],
    private _loader?: PlaceResultLoader,
    private _prev?: PlaceResultWrapper
  ) {
    if (!this._loader) {
      this._loader = new PlaceResultLoader(this._retrieve, this._handler);
    }
  }

  private async _getResults(): Promise<PlaceResultWrapper> {
    const result = await this._loader.getResults();
    this.places = result.places;
    this.pagination = result.pagination;
    return this;
  }

  /**
   * Returns true if there is a previous page
   */
  get hasPrevPage(): boolean {
    return !!this._prev;
  }

  /**
   * Returns the PlaceResultWrapper corresponding to the previous page (if available).
   */
  getPrevPage(): PlaceResultWrapper {
    return this._prev;
  }

  /**
   * Returns true if there is a next page
   */
  get hasNextPage(): boolean {
    return this.pagination && this.pagination.hasNextPage;
  }

  /**
   * Returns a promise that resolves to the PlaceResultWrapper corresponding to the next page of results.
   */
  async getNextPage(): Promise<PlaceResultWrapper> {
    if (!this.hasNextPage) {
      return null;
    }
    const page = await this._loader.loadPage(this.pagination);
    const result = new PlaceResultWrapper(this._retrieve, this._handler, this._loader, this);
    result.places = page.places;
    result.pagination = page.pagination;
    return result;
  }
}
